import { useState } from 'react';
import {
  Button,
  InlineAlert,
  LoadingButton,
  Typography,
  AlertOutlineIcon,
} from '@eventbrite/marmalade';
import { useIntl } from 'react-intl';
import { CONTACT_REASONS, API_ERROR_MAP, messages } from './constants';
import type { ContactFormData } from './types';
import styles from './ConfirmationView.module.scss';

interface Props {
  formData: ContactFormData;
  organizerId: string;
  onEdit: () => void;
  onSuccess: () => void;
}

export function ConfirmationView({ formData, organizerId, onEdit, onSuccess }: Props) {
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const matchedReason = CONTACT_REASONS.find((r) => r.value === formData.reason);
  const reasonLabel = matchedReason
    ? intl.formatMessage(messages[matchedReason.messageKey])
    : formData.reason;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setApiError(null);

    try {
      const csrfToken = document.cookie
        .split('; ')
        .find((c) => c.startsWith('csrftoken='))
        ?.split('=')[1];

      const eventIdParam = new URLSearchParams(window.location.search).get('event_id');

      const res = await fetch(`/api/v3/organizers/${organizerId}/contact/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
        },
        body: JSON.stringify({
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          reason: formData.reason,
          ...(eventIdParam ? { event_id: eventIdParam } : {}),
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const errorCode = (body.errorCode as string) || 'DEFAULT';
        const messageKey = API_ERROR_MAP[errorCode] ?? 'errorBadRequest';
        setApiError(intl.formatMessage(messages[messageKey]));
        return;
      }

      onSuccess();
    } catch {
      setApiError(intl.formatMessage(messages.errorBadRequest));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.root}>
      {apiError && (
        <InlineAlert
          variant="danger"
          description={apiError}
          icon={
            <span>
              <AlertOutlineIcon />
            </span>
          }
          closeOnClick={() => setApiError(null)}
          closeTitle={intl.formatMessage(messages.closeModal)}
        />
      )}

      <section>
        <Typography variant="body-md-bold">
          {intl.formatMessage(messages.contactInfoHeading)}
        </Typography>
        <div className={styles.fieldRow}>
          <div className={styles.fieldGroup}>
            <Typography variant="body-md" color="neutral-600">
              {intl.formatMessage(messages.yourName)}
            </Typography>
            <Typography variant="body-md">{formData.name}</Typography>
          </div>
          <div className={styles.fieldGroup}>
            <Typography variant="body-md" color="neutral-600">
              {intl.formatMessage(messages.yourEmail)}
            </Typography>
            <Typography variant="body-md">{formData.email}</Typography>
          </div>
        </div>
      </section>

      <section>
        <Typography variant="body-md-bold">{intl.formatMessage(messages.contactReason)}</Typography>
        <Typography variant="body-md">{reasonLabel}</Typography>
      </section>

      <section>
        <Typography variant="body-md-bold">{intl.formatMessage(messages.messageLabel)}</Typography>
        <Typography variant="body-md">{formData.message}</Typography>
      </section>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onEdit} disabled={isSubmitting}>
          {intl.formatMessage(messages.editInformation)}
        </Button>
        <LoadingButton variant="primary" isLoading={isSubmitting} onClick={handleSubmit}>
          {intl.formatMessage(messages.submitRequest)}
        </LoadingButton>
      </div>
    </div>
  );
}
